import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEye, FiPlay, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import cierreContableService from '../services/cierreContableService';
import Loading from '../components/Loading';
import './CierreContablePage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const CierreContablePage = () => {
  const [cierres, setCierres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState({ open: false, data: null, cierreId: null });

  const [form, setForm] = useState({
    tipo: 'mensual', anio: new Date().getFullYear(), mes: new Date().getMonth() + 1,
    fecha_cierre: '', cuenta_resultado: '',
  });

  useEffect(() => { cargarCierres(); }, []);

  const cargarCierres = async () => {
    try {
      setLoading(true);
      const res = await cierreContableService.getAll();
      setCierres(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar cierres'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await cierreContableService.create(form);
      toast.success('Cierre creado');
      setModalOpen(false);
      cargarCierres();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear cierre'); }
  };

  const handlePreview = async (id) => {
    try {
      const res = await cierreContableService.preview(id);
      setPreviewModal({ open: true, data: res.data, cierreId: id });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cargar preview'); }
  };

  const handleEjecutar = async (id) => {
    if (!window.confirm('¿Ejecutar este cierre contable? Se generara un asiento de cierre automaticamente.')) return;
    try {
      await cierreContableService.ejecutar(id);
      toast.success('Cierre ejecutado exitosamente');
      setPreviewModal({ open: false, data: null, cierreId: null });
      cargarCierres();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al ejecutar cierre'); }
  };

  const handleReabrir = async (id) => {
    if (!window.confirm('¿Reabrir este periodo? El asiento de cierre sera revertido.')) return;
    try {
      await cierreContableService.reabrir(id);
      toast.success('Periodo reabierto');
      cargarCierres();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al reabrir'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cierre?')) return;
    try { await cierreContableService.delete(id); toast.success('Eliminado'); cargarCierres(); }
    catch { toast.error('Error al eliminar'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const meses = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const getEstadoBadge = (e) => {
    const map = { borrador: 'badge-gray', cerrado: 'badge-green', reabierto: 'badge-yellow' };
    return map[e] || 'badge-gray';
  };

  if (loading) return <Loading />;

  const preview = previewModal.data;

  return (
    <div className="cierre-page">
      <div className="page-header">
        <h1>Cierre Contable</h1>
        <button className="btn-primary" onClick={() => setModalOpen(true)}><FiPlus /> Nuevo Cierre</button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr><th>Tipo</th><th>Periodo</th><th>Fecha Cierre</th><th style={{ textAlign: 'right' }}>Ingresos</th><th style={{ textAlign: 'right' }}>Gastos</th><th style={{ textAlign: 'right' }}>Resultado</th><th>Estado</th><th style={{ textAlign: 'right' }}>Acciones</th></tr>
          </thead>
          <tbody>
            {cierres.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin cierres registrados</td></tr>
            ) : cierres.map(c => (
              <tr key={c.id}>
                <td><span className="badge badge-blue">{c.tipo}</span></td>
                <td><strong>{c.tipo === 'mensual' ? `${meses[c.mes]} ${c.anio}` : `${c.anio}`}</strong></td>
                <td>{c.fecha_cierre}</td>
                <td className="mono right" style={{ color: '#059669' }}>{formatCurrency(c.total_ingresos)}</td>
                <td className="mono right" style={{ color: '#dc2626' }}>{formatCurrency(c.total_gastos)}</td>
                <td className="mono right bold" style={{ color: parseFloat(c.resultado || 0) >= 0 ? '#059669' : '#dc2626' }}>
                  {formatCurrency(c.resultado)}
                </td>
                <td><span className={`badge ${getEstadoBadge(c.estado)}`}>{c.estado}</span></td>
                <td>
                  <div className="acciones-cell">
                    <button className="btn-icon" title="Vista Previa" onClick={() => handlePreview(c.id)}><FiEye /></button>
                    {(c.estado === 'borrador' || c.estado === 'reabierto') && (
                      <button className="btn-icon btn-success" title="Ejecutar Cierre" onClick={() => handleEjecutar(c.id)}><FiPlay /></button>
                    )}
                    {c.estado === 'cerrado' && (
                      <button className="btn-icon btn-warning" title="Reabrir" onClick={() => handleReabrir(c.id)}><FiRotateCcw /></button>
                    )}
                    {c.estado !== 'cerrado' && (
                      <button className="btn-icon btn-danger" title="Eliminar" onClick={() => handleDelete(c.id)}><FiTrash2 /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Crear */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Nuevo Cierre Contable</h2>
            <div className="form-grid">
              <div className="form-group"><label>Tipo</label>
                <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                  <option value="mensual">Mensual</option><option value="anual">Anual</option>
                </select>
              </div>
              <div className="form-group"><label>Ano</label>
                <select value={form.anio} onChange={e => setForm({ ...form, anio: parseInt(e.target.value) })}>
                  {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              {form.tipo === 'mensual' && (
                <div className="form-group"><label>Mes</label>
                  <select value={form.mes} onChange={e => setForm({ ...form, mes: parseInt(e.target.value) })}>
                    {meses.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label>Fecha Cierre</label>
                <input type="date" value={form.fecha_cierre} onChange={e => setForm({ ...form, fecha_cierre: e.target.value })} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleCreate}>Crear Cierre</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Preview */}
      {previewModal.open && preview && (
        <div className="modal-overlay" onClick={() => setPreviewModal({ open: false, data: null, cierreId: null })}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <h2>Vista Previa del Cierre</h2>
            <table className="data-table">
              <thead><tr><th>Cuenta</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Saldo</th></tr></thead>
              <tbody>
                {(preview.cuentas || []).map((c, i) => (
                  <tr key={i}>
                    <td>{c.codigo} {c.nombre}</td>
                    <td><span className={`badge ${c.tipo === 'INGRESO' ? 'badge-green' : 'badge-red'}`}>{c.tipo}</span></td>
                    <td className="mono right" style={{ color: c.tipo === 'INGRESO' ? '#059669' : '#dc2626' }}>{formatCurrency(c.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="preview-resultado">
              <div><span>Total Ingresos:</span><strong style={{ color: '#059669' }}>{formatCurrency(preview.total_ingresos)}</strong></div>
              <div><span>Total Gastos:</span><strong style={{ color: '#dc2626' }}>{formatCurrency(preview.total_gastos)}</strong></div>
              <div className="resultado-final" style={{ color: parseFloat(preview.resultado || 0) >= 0 ? '#059669' : '#dc2626' }}>
                <span>RESULTADO:</span>
                <strong>{formatCurrency(preview.resultado)} ({parseFloat(preview.resultado || 0) >= 0 ? 'UTILIDAD' : 'PERDIDA'})</strong>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setPreviewModal({ open: false, data: null, cierreId: null })}>Cancelar</button>
              <button className="btn-primary" onClick={() => handleEjecutar(previewModal.cierreId)}><FiPlay /> Ejecutar Cierre</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CierreContablePage;
